import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import LoadingSpinner from './LoadingSpinner';
import authService from '../services/authService';

// Contact Form Component with reCAPTCHA v3
const ContactForm = ({ show, onClose, backendBaseURL }) => {
  // Contact follows light/dark theme only once authenticated; from the landing
  // page (pre-login) it stays dark like the rest of the auth flow.
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  // Contact form state
  const [contactData, setContactData] = useState({ fullName: '', email: '', message: '' });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Clear form and messages when pane opens
  useEffect(() => {
    if (show) {
      // Clear messages when contact form is opened
      setContactSuccess(false);
      setContactError('');
    }
  }, [show]);

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Contact form submitted, validating...');
    
    // Validate form
    if (!contactData.fullName.trim() || !contactData.email.trim() || !contactData.message.trim()) {
      setContactError('All fields are required');
      return;
    }
    
    if (contactData.message.trim().length < 10) {
      setContactError('Message must be at least 10 characters');
      return;
    }
    
    console.log('Validation passed, executing reCAPTCHA v3...');
    
    if (!executeRecaptcha) {
      setContactError('reCAPTCHA not available. Please try again.');
      return;
    }
    
    setIsSubmittingContact(true);
    setContactError('');
    
    try {
      // Execute reCAPTCHA v3
      const captchaToken = await executeRecaptcha('contact_form');
      console.log('reCAPTCHA v3 token received:', captchaToken ? 'Yes' : 'No');
      
      if (!captchaToken) {
        throw new Error('reCAPTCHA verification failed');
      }
      
      console.log('Submitting contact form with reCAPTCHA v3 token...');
      const authToken = await authService.getToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      const apiBaseURL = backendBaseURL || 'https://api.ask-cara.com';
      console.log('API URL:', `${apiBaseURL}/api/v1/contact`);
      
      const response = await fetch(`${apiBaseURL}/api/v1/contact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fullName: contactData.fullName.trim(),
          email: contactData.email.trim(),
          message: contactData.message.trim(),
          captchaToken: captchaToken
        })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', errorData);
        throw new Error(errorData.message || `Failed to send message: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Contact form submitted successfully:', result);
      
      setContactSuccess(true);
      setContactData({ fullName: '', email: '', message: '' });
      
      // Don't auto-close - let user manually close the modal
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setContactError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  // If not showing, don't render anything
  if (!show) return null;

  return (
    <motion.div
      className={`fixed inset-0 z-[9999]${isAuthenticated ? ' cara-chrome' : ''}`}
      style={{ fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Left Backdrop - NOT clickable (users must use X button to close) */}
      <motion.div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Right Pane - 49% width on desktop, full width on mobile */}
      <motion.div
        className="absolute right-0 top-0 bottom-0 w-full md:w-[49%] bg-[#1C2837] overflow-y-auto"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          background: `
            radial-gradient(circle at 80% 20%, rgba(68, 194, 253, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 20% 80%, rgba(35, 166, 213, 0.06) 0%, transparent 50%),
            linear-gradient(180deg, #1C2837 0%, #1C2837 100%)
          `,
        }}
      >
        <div className="min-h-full flex flex-col relative">
          {/* Header with Close Button */}
          <div className="border-b border-gray-700/30 p-8">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <img 
                src="/images/cara-white.png" 
                alt="Cara" 
                className="h-20 w-auto brightness-110"
              />
              <button
                onClick={onClose}
                className="text-white hover:text-[#44C2FD] transition-colors p-2 rounded-full hover:bg-white/10"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-2xl">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="text-3xl font-bold text-white mb-2">Contact Us</h2>
                <p className="text-gray-300 mb-8">
                  Want to learn more? Send us a message and our team will respond within 24 hours.
                </p>

                {/* Success Message */}
                {contactSuccess && (
                  <motion.div
                    className="bg-gradient-to-r from-green-900/40 to-green-800/40 border border-green-500/50 text-green-100 px-5 py-4 rounded-lg mb-6 backdrop-blur-sm"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-semibold">Message sent successfully!</p>
                        <p className="text-sm text-green-200 mt-0.5">We'll get back to you soon.</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Error Message */}
                {contactError && (
                  <motion.div
                    className="bg-gradient-to-r from-red-900/40 to-red-800/40 border border-red-500/50 text-red-100 px-5 py-4 rounded-lg mb-6 backdrop-blur-sm"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium">{contactError}</p>
                    </div>
                  </motion.div>
                )}

                <form
                  onSubmit={handleContactFormSubmit}
                  className="space-y-6"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      required
                      value={contactData.fullName}
                      onChange={(e) => {
                        setContactData({ ...contactData, fullName: e.target.value });
                        setContactError('');
                      }}
                      disabled={isSubmittingContact}
                      className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={contactData.email}
                      onChange={(e) => {
                        setContactData({ ...contactData, email: e.target.value });
                        setContactError('');
                      }}
                      disabled={isSubmittingContact}
                      className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message
                    </label>
                    <textarea
                      required
                      value={contactData.message}
                      onChange={(e) => {
                        setContactData({ ...contactData, message: e.target.value });
                        setContactError('');
                      }}
                      disabled={isSubmittingContact}
                      rows={3}
                      minLength={10}
                      className="w-full px-4 py-3 bg-[#0f1620] border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#44c2fd] focus:border-transparent resize-vertical transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="Tell us about your enquiry..."
                    />
                    <p className="text-xs text-gray-500 mt-1.5">Minimum 10 characters</p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmittingContact}
                      className="flex-1 bg-transparent text-white font-semibold py-3 px-6 rounded-lg border-2 border-[#44C2FD] hover:bg-[#44C2FD]/20 hover:border-[#3AB0E8] transition-all duration-300 uppercase tracking-wide relative group disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        boxShadow: '0 0 20px rgba(68, 194, 253, 0.15)',
                      }}
                    >
                      <span className="relative z-10">Cancel</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-[#44C2FD]/0 via-[#44C2FD]/30 to-[#44C2FD]/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingContact}
                      className="flex-1 bg-[#44c2fd] hover:bg-[#3bb0eb] text-white font-semibold py-3 px-6 rounded-lg transition-all uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmittingContact ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">Sending...</span>
                        </>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setContactData({ fullName: '', email: '', message: '' });
                        setContactError('');
                        setContactSuccess(false);
                      }}
                      disabled={isSubmittingContact}
                      className="text-[#44c2fd] hover:text-[#3bb0eb] transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Clear 
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main ContactPane component with reCAPTCHA v3 provider
const ContactPane = ({ show, onClose }) => {
  // Get backend URL from Redux state for API calls
  const backendBaseURL = useSelector((state) => state.channel.backendBaseURL);
  
  // reCAPTCHA v3 site key
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LclGccsAAAAAFwOX0-42fFaeoNolUZVlgJXL0oe';

  // If not showing, don't render anything
  if (!show) return null;

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'head',
        nonce: undefined,
      }}
    >
      <ContactForm 
        show={show} 
        onClose={onClose} 
        backendBaseURL={backendBaseURL} 
      />
    </GoogleReCaptchaProvider>
  );
};

export default ContactPane;
